import React, { useState, useEffect, useCallback } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { EmblaOptionsType } from "embla-carousel";
import { DotButton, useDotButton } from "./EmblaCarouselDotButton";
import {
  PrevButton,
  NextButton,
  usePrevNextButtons,
} from "./EmblaCarouselArrowButtons";

type SlidesType = {
  imagesrc: string;
  title: string;
  description: string;
  buttonHref: string;
};

type PropType = {
  slides: SlidesType[];
  options?: EmblaOptionsType;
  buttonText: string;
};

export const EmblaCarousel: React.FC<PropType> = (props) => {
  const { slides, options, buttonText } = props;
  const [emblaRef, emblaApi] = useEmblaCarousel(options);
  const { selectedIndex, scrollSnaps, onDotButtonClick } =
    useDotButton(emblaApi);

  const {
    prevBtnDisabled,
    nextBtnDisabled,
    onPrevButtonClick,
    onNextButtonClick,
  } = usePrevNextButtons(emblaApi);

  return (
    <section className="bg-gray-100 py-8 ">
      <div className="embla mx-auto px-8">
        <div className="embla__viewport" ref={emblaRef}>
          <div className="embla__container">
            {slides.map((slide, index) => {
              const { imagesrc, title, description, buttonHref } = slide;

              // Mark specific slides as "Coming Soon"
              const isComingSoon = index >= 2 && index <= 6;

              return (
                <div
                  className="embla__slide min-w-0 bg-white rounded-lg relative"
                  key={index}
                  data-index={index}
                >
                  {/* Slide Image Wrapper */}
                  <div className="relative">
                    {/* GSMA API Certified Tag (Higher z-index) */}
                    {!isComingSoon && ( 
                    <div className="absolute top-0 left-0 bg-red-100 text-white text-xs px-3 py-1 rounded-br-lg rounded-tl-lg z-30 flex items-center">
                      <a
                        href="https://open-gateway.gsma.com/"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <div className="w-12 h-4 mr-2">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 1920 300"
                            className="w-full h-full"
                          >
                            <path
                              d="M438.73 133.47v33.76c0 41.2-9.86 67.11-30.39 87.54-27.6 27.46-79.19 46.02-179.16 46.02-115.99 0-164.29-23.78-191.56-51C13.8 226.08 0 192.81 0 150.39c0-40.13 12.49-74.71 37.62-99.72C64.81 23.62 119.27 0 229.92 0c87.89 0 153.2 14.55 188.36 30.9v60.16C376.55 69.8 309.77 53.62 229.59 53.62c-78.94 0-120.59 14.06-138.9 32.28-15.03 14.96-22.75 35.72-22.75 64.49 0 30.24 8.3 50.43 22.43 64.57 18.73 18.63 54.38 33.76 138.82 33.76 67.28 0 111.22-11.03 128.06-27.79 10.93-10.87 14.21-22.72 14.13-35.47H194.6v-51.98l244.13-.01zM505.9 34.72C527.05 13.67 568.69 0 670.28 0 749.9 0 813.6 11.6 852.33 26.93v57.01C808.19 65.3 734.16 51.62 670.45 51.62c-77.7 0-102.1 6.79-111.59 16.24-4.66 4.64-6.75 10.77-6.75 18.15 0 7.13 2 12.93 6 16.9 8.74 8.7 30.98 13.17 122.08 17.15 120.58 5.47 157.56 14.67 178.21 35.22 11.74 11.68 18.07 29.83 18.07 50.55 0 21.71-4.58 41.93-20.49 57.75-20.99 20.88-63.46 37.21-173.3 37.21-81.28 0-160.64-12.84-205.53-32.15v-57.01c43.22 20.38 135.49 37.54 205.53 37.54 77.86 0 107.67-5.72 119.42-17.4 5.5-5.47 7.33-12.01 7.33-18.89 0-8.04-1.92-14.09-6.58-18.73-8.66-8.62-24.57-12.51-126.91-17.4-114.25-5.22-153.06-14.91-173.96-35.71-11.58-11.52-16.9-28.09-16.9-48.89 0-21.3 4.5-41.19 20.82-57.43zM1322.85 7.36 1166.59 252.4 1010.49 7.36h-87.2v286.07h66.13V85.68l130.67 207.75h90.36l130.38-206.75.22 206.75h67.11V7.36zM1582.64 176.79h179.98L1672.5 45.45l-89.86 131.34zm136.6-169.43L1920 293.43h-77.34l-44.06-64.24h-251.86l-44.04 64.24h-74.25L1629.21 7.36h90.03zM1846.95 50.24h-7.61V14.29h-13.62V7.3h34.84v6.99h-13.62v35.95h.01zM1886.9 39.69h-.24l-13.43-20.12v30.67h-7.42V7.3h8.04l13.07 20.3 13.07-20.3h8.04v42.94h-7.54V19.45l-13.59 20.24z"
                              fill="white"
                            ></path>
                          </svg>
                        </div>
                      </a>
                      API Certified
                    </div>
                    )}

                    {/* "Coming Soon" Overlay (Only on Image) */}
                    {isComingSoon && (
                      <div className="absolute rounded-tl-lg rounded-tr-lg inset-0 bg-gray-800 bg-opacity-75 flex items-center justify-center z-20">
                        <span className="text-white font-bold text-lg md:text-xl">
                          Coming Soon
                        </span>
                      </div>
                    )}

                    {/* Slide Image */}
                    <div className="rounded-tl-lg rounded-tr-lg h-40 md:h-48 w-full relative overflow-hidden">
                      <img
                        className="embla__slide__img w-full h-full object-cover"
                        src={imagesrc || "/placeholder.svg"}
                        alt={`Slide ${index + 1}`}
                      />
                    </div>
                  </div>

                  {/* Slide Content */}
                  <div className="flex flex-col p-6 h-64  ">
                    {/* Content at the top */}
                    <div className="flex-1">
                      <h2 className="text-xl md:text-2xl font-bold mb-2">
                        {title}
                      </h2>
                      <p className="text-sm md:text-base text-gray-600">
                        {description}
                      </p>
                    </div>

                    {/* Button at the bottom */}
                    <div className="mt-auto">
                      <button
                        onClick={() => {
                          window.open(buttonHref, "_blank");
                        }}
                        className="bg-orange-100 hover:bg-orange-200 text-white font-bold py-2 px-4 rounded transition-colors duration-300 text-sm md:text-base"
                      >
                        Explore
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="embla__controls">
          <div className="embla__buttons">
            <PrevButton
              onClick={onPrevButtonClick}
              disabled={prevBtnDisabled}
            />
            <NextButton
              onClick={onNextButtonClick}
              disabled={nextBtnDisabled}
            />
          </div>

          <div className="embla__dots">
            {scrollSnaps.map((_, index) => (
              <DotButton
                key={index}
                onClick={() => onDotButtonClick(index)}
                className={"embla__dot".concat(
                  index === selectedIndex ? " embla__dot--selected" : ""
                )}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
